import { createInstance, dateReviver } from '@/api';
import {
    SourceEntityCreateDto,
    SourceEntityClient,
    SourceEntityDetailDto,
    SourceEntityUpdateDto,
    DataConnectionClient
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from '../product-process-definition/product-process-definition-edit.store';
import { useProductTransformationStore } from '../product-transformation/product-transformation.store';

export interface sourceentityStoreState {
    items?: { key: string; value: SourceEntityDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new SourceEntityClient('', createInstance(), dateReviver);
export const dataConnectionClient = new DataConnectionClient('', createInstance(), dateReviver);

export const useSourceEntityStore = defineStore('configuration-source-entity-store', {
    state: (): sourceentityStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): SourceEntityDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        },
        bySpecification: (state) => {
            return (specificationId: number): SourceEntityDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async updateSourceEntityUI(items: SourceEntityDetailDto[]) {
            items?.forEach((item) => {
                useProductTransformationStore().updateSourceEntityUI(item);
            });
        },
        async getByDefinition(defintionId: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const definitionKey = `definition-${defintionId}`;
            try {
                const response = await client.getByDelivery(defintionId);
                const items = this.items.filter((x) => x.key !== definitionKey);
                if (response?.length) {
                    this.items = [...items, { key: definitionKey, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateSourceEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('sourceentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async getBySpecification(specificationId: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `specification-${specificationId}`;

            try {
                const response = await client.getBySpecification(specificationId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateSourceEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('sourceentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(specificationId: number, dto: SourceEntityCreateDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                this.getBySpecification(specificationId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('sourceentity.detail.error.create.title'), error);
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(specificationId: number, entityId: number, dto: SourceEntityUpdateDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(entityId, dto);
                useAlertStore().setSuccess(i18n.global.t('sourceentity.detail.success.update.title'));
                this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('sourceentity.detail.error.update.title'), error);
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(specificationId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('sourceentity.detail.success.delete.title'));
                await this.getBySpecification(specificationId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('sourceentity.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(specificationId: number) {
            const key = `specification-${specificationId}`;
            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
